import React, { useContext, useEffect } from 'react';
import { X } from 'lucide-react';
import { SidebarContext } from '../contexts/SidebarContext';
import SpreadsheetViewer from './SpreadsheetViewer';

// Spreadsheets get the in-app SheetJS renderer — iframing the raw URL would
// download it, and the Office web viewer can't fetch Firebase Storage URLs.
const isSpreadsheetUrl = (url) => {
  try {
    return /\.(xlsx?|csv)$/i.test(decodeURIComponent(new URL(url).pathname));
  } catch {
    return /\.(xlsx?|csv)([?#]|$)/i.test(url);
  }
};

const isImageName = (name) => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name || '');

/**
 * Fullscreen in-page document viewer: the file fills the viewport (PDFs
 * natively in an iframe, spreadsheets via the in-app SheetJS renderer) with a
 * single floating close button — no chrome, borders, or new tab. Inside the
 * portal layout its left edge sits flush against the sidebar (matching
 * MainContent's 256px/80px offset); without a SidebarProvider it goes
 * edge-to-edge.
 *
 * Pass `fileName` for URLs with no file extension in them (e.g. blob: URLs of
 * email attachments) so spreadsheets and images still get the right viewer.
 */
const DocumentViewerModal = ({ isOpen, onClose, url, title, fileName }) => {
  const sidebar = useContext(SidebarContext);
  const leftOffset = sidebar ? (sidebar.isCollapsed ? 80 : 256) : 0;
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen || !url) return null;

  return (
    <div
      className="fixed inset-y-0 right-0 z-[100] transition-[left] duration-300"
      style={{ left: leftOffset }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close document viewer"
        title="Close (Esc)"
        className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-gray-900/80 text-white flex items-center justify-center hover:bg-gray-900 transition-colors shadow-lg"
      >
        <X className="w-5 h-5" />
      </button>
      {isSpreadsheetUrl(url) || /\.(xlsx?|csv)$/i.test(fileName || '') ? (
        <SpreadsheetViewer url={url} title={title || 'Spreadsheet'} downloadName={fileName} />
      ) : isImageName(fileName) ? (
        <div className="w-full h-full bg-gray-950/95 flex items-center justify-center p-6">
          <img src={url} alt={title || fileName} className="max-w-full max-h-full object-contain shadow-2xl" />
        </div>
      ) : (
        <iframe title={title || 'Document'} src={url} className="w-full h-full border-0 bg-white" />
      )}
    </div>
  );
};

export default DocumentViewerModal;
